import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Modal, Field, Btn, Pill, Empty, Notice, inputCls } from "@/dashboard/ui";
import PhoneInput from "@/components/PhoneInput";
import { Plus, Pencil, UserCheck, Briefcase } from "lucide-react";

const DAY_LABELS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const rangesToText = (ranges) => (ranges || []).map((r) => `${r.start}-${r.end}`).join(", ");

function textToRanges(text) {
  const t = (text || "").trim();
  if (!t) return [];
  const out = [];
  for (const part of t.split(",")) {
    const m = part.trim().match(/^(\S{4,5})-(\S{4,5})$/);
    if (!m || !TIME_RE.test(m[1]) || !TIME_RE.test(m[2])) {
      throw new Error(`"${part.trim()}" is not a valid range — use HH:MM-HH:MM, e.g. 09:00-12:30`);
    }
    out.push({ start: m[1], end: m[2] });
  }
  return out;
}

const EMPTY_STAFF = { name: "", role: "", email: "", phone: "", active: true, hoursText: {}, timeOff: [] };

function staffToForm(s) {
  const hoursText = {};
  DAY_LABELS.forEach(([k]) => { hoursText[k] = rangesToText(s.working_hours?.[k]); });
  return {
    name: s.name || "", role: s.role || "", email: s.email || "", phone: s.phone || "",
    active: s.active !== false, hoursText,
    timeOff: (s.time_off || []).map((t) => ({ date: t.date, start: t.start || "", end: t.end || "" })),
  };
}

function StaffModal({ staffMember, allStaff, onClose, onSaved }) {
  const [form, setForm] = useState(staffMember ? staffToForm(staffMember) : { ...EMPTY_STAFF, hoursText: { mon: "09:00-17:00", tue: "09:00-17:00", wed: "09:00-17:00", thu: "09:00-17:00", fri: "09:00-17:00", sat: "", sun: "" }, timeOff: [] });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    let working_hours = {};
    let time_off = [];
    try {
      DAY_LABELS.forEach(([k]) => { working_hours[k] = textToRanges(form.hoursText[k]); });
      time_off = form.timeOff
        .filter((t) => t.date)
        .map((t) => {
          if ((t.start && !TIME_RE.test(t.start)) || (t.end && !TIME_RE.test(t.end))) throw new Error("Time-off times must be HH:MM");
          if (t.start && !t.end) throw new Error("Time off with a start time also needs an end time (or leave both empty for a full day)");
          return { date: t.date, start: t.start || null, end: t.end || null };
        });
    } catch (err) {
      setError(err.message);
      return;
    }
    setBusy(true);
    const payload = {
      name: form.name, role: form.role || null, email: form.email || null, phone: form.phone || null,
      active: form.active, working_hours, time_off,
    };
    try {
      if (staffMember) await api.put(`/staff/${staffMember.id}`, payload);
      else await api.post("/staff", payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="staff-modal" title={staffMember ? `Edit ${staffMember.name}` : "Add staff member"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Display name *">
            <input data-testid="staff-name-input" className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </Field>
          <Field label="Role / title">
            <input data-testid="staff-role-input" className={inputCls} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} placeholder="e.g. Senior stylist" />
          </Field>
          <Field label="Email">
            <input data-testid="staff-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label="Phone">
            <PhoneInput testid="staff-phone-input" label="" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
          </Field>
        </div>
        <label className="flex items-center gap-2.5 text-sm font-medium text-neutral-700">
          <input type="checkbox" data-testid="staff-active-checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-[#111111]" />
          Active — can receive new bookings
        </label>

        <div>
          <p className="mb-1.5 text-xs font-semibold text-neutral-600">Weekly working hours</p>
          <p className="mb-2 text-[11px] text-neutral-400">One or more ranges per day, e.g. 09:00-12:00, 13:00-17:00. Leave empty for a non-working day. An end earlier than the start counts as an overnight shift.</p>
          <div className="space-y-1.5">
            {DAY_LABELS.map(([k, label]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="w-20 shrink-0 text-xs font-medium text-neutral-500">{label}</span>
                <input data-testid={`staff-hours-${k}`} className={inputCls} placeholder="Off" value={form.hoursText[k] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, hoursText: { ...f.hoursText, [k]: e.target.value } }))} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-xs font-semibold text-neutral-600">Time off / unavailability</p>
            <Btn type="button" variant="ghost" data-testid="timeoff-add-button"
              onClick={() => setForm((f) => ({ ...f, timeOff: [...f.timeOff, { date: "", start: "", end: "" }] }))}>
              <Plus size={13} /> Add
            </Btn>
          </div>
          {form.timeOff.length === 0 && <p className="text-[11px] text-neutral-400">None. Full days: set a date only. Partial: add start and end times.</p>}
          <div className="space-y-1.5">
            {form.timeOff.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`timeoff-row-${i}`}>
                <input type="date" className={inputCls} value={t.date} data-testid={`timeoff-date-${i}`}
                  onFocus={(e) => e.target.scrollIntoView({ block: "center", behavior: "smooth" })}
                  onChange={(e) => setForm((f) => ({ ...f, timeOff: f.timeOff.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)) }))} />
                <input type="time" className={inputCls} value={t.start} data-testid={`timeoff-start-${i}`}
                  onChange={(e) => setForm((f) => ({ ...f, timeOff: f.timeOff.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)) }))} />
                <input type="time" className={inputCls} value={t.end} data-testid={`timeoff-end-${i}`}
                  onChange={(e) => setForm((f) => ({ ...f, timeOff: f.timeOff.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)) }))} />
                <button type="button" aria-label="Remove time off" data-testid={`timeoff-remove-${i}`}
                  onClick={() => setForm((f) => ({ ...f, timeOff: f.timeOff.filter((_, j) => j !== i) }))}
                  className="shrink-0 text-neutral-400 hover:text-red-600">×</button>
              </div>
            ))}
          </div>
        </div>

        {error && <p data-testid="staff-form-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2.5">
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="staff-save-button" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Staff Member"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

export function StaffPanel({ staff, onChanged }) {
  const [modal, setModal] = useState(null);
  return (
    <div data-testid="staff-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-neutral-500">Staff are scheduling resources managed by your account — they don't need their own login.</p>
        <Btn data-testid="staff-add-button" onClick={() => setModal({ mode: "add" })}><Plus size={15} /> Add Staff</Btn>
      </div>
      {staff.length === 0 ? (
        <Empty testid="staff-empty" icon={UserCheck} title="No staff yet" body="Add your first team member, set their working hours, then qualify them for services." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {staff.map((s) => {
            const days = DAY_LABELS.filter(([k]) => (s.working_hours?.[k] || []).length > 0).map(([, l]) => l.slice(0, 3)).join(" ");
            return (
              <Card key={s.id} className="p-5" data-testid={`staff-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <p className="text-sm font-bold text-[#111111]">{s.name}</p>
                      <Pill tone={s.active ? "black" : "outline"}>{s.active ? "Active" : "Inactive"}</Pill>
                    </div>
                    {s.role && <p className="mt-0.5 text-xs text-neutral-500">{s.role}</p>}
                    <p className="mt-1.5 text-xs text-neutral-500">Works: {days || "no days set"}</p>
                    {(s.time_off || []).length > 0 && <p className="text-xs text-neutral-400">{s.time_off.length} time-off entr{s.time_off.length === 1 ? "y" : "ies"}</p>}
                  </div>
                  <button data-testid={`staff-edit-${s.id}`} aria-label="Edit staff" onClick={() => setModal({ mode: "edit", staff: s })}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                    <Pencil size={14} />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {modal && <StaffModal staffMember={modal.mode === "edit" ? modal.staff : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />}
    </div>
  );
}

const EMPTY_SERVICE = { name: "", description: "", duration_min: 30, buffer_before_min: 0, buffer_after_min: 0, active: true, staff_ids: [] };

function ServiceModal({ service, staff, onClose, onSaved }) {
  const [form, setForm] = useState(service ? {
    name: service.name, description: service.description || "", duration_min: service.duration_min,
    buffer_before_min: service.buffer_before_min || 0, buffer_after_min: service.buffer_after_min || 0,
    active: service.active !== false, staff_ids: service.staff_ids || [],
  } : EMPTY_SERVICE);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleStaff = (id) =>
    setForm((f) => ({ ...f, staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter((x) => x !== id) : [...f.staff_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { ...form, duration_min: Number(form.duration_min), buffer_before_min: Number(form.buffer_before_min), buffer_after_min: Number(form.buffer_after_min), description: form.description || null };
    try {
      if (service) await api.put(`/services/${service.id}`, payload);
      else await api.post("/services", payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="service-modal" title={service ? `Edit ${service.name}` : "Add service"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Service name *">
          <input data-testid="service-name-input" className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
        </Field>
        <Field label="Description">
          <input data-testid="service-description-input" className={inputCls} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Duration (min) *">
            <input data-testid="service-duration-input" type="number" min={5} max={480} className={inputCls} value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))} required />
          </Field>
          <Field label="Buffer before (min)">
            <input data-testid="service-buffer-before-input" type="number" min={0} max={180} className={inputCls} value={form.buffer_before_min} onChange={(e) => setForm((f) => ({ ...f, buffer_before_min: e.target.value }))} />
          </Field>
          <Field label="Buffer after (min)">
            <input data-testid="service-buffer-after-input" type="number" min={0} max={180} className={inputCls} value={form.buffer_after_min} onChange={(e) => setForm((f) => ({ ...f, buffer_after_min: e.target.value }))} />
          </Field>
        </div>
        <Field label="Qualified staff — only they can receive bookings for this service">
          <div className="space-y-1 rounded-xl border border-neutral-200 p-2" data-testid="service-staff-picker">
            {staff.length === 0 && <p className="px-2 py-2 text-xs text-neutral-400">Add staff first in the Staff tab.</p>}
            {staff.map((st) => (
              <label key={st.id} data-testid={`service-staff-${st.id}`} className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs hover:bg-neutral-50 ${st.active ? "" : "opacity-50"}`}>
                <input type="checkbox" checked={form.staff_ids.includes(st.id)} onChange={() => toggleStaff(st.id)} className="h-3.5 w-3.5 accent-[#111111]" />
                <span className="font-semibold text-[#111111]">{st.name}</span>
                {!st.active && <span className="text-neutral-400">(inactive — can't be booked)</span>}
              </label>
            ))}
          </div>
        </Field>
        <label className="flex items-center gap-2.5 text-sm font-medium text-neutral-700">
          <input type="checkbox" data-testid="service-active-checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-[#111111]" />
          Active — can be selected for new bookings
        </label>
        {error && <p data-testid="service-form-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2.5">
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="service-save-button" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Service"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

export function ServicesPanel({ services, staff, onChanged }) {
  const [modal, setModal] = useState(null);
  const staffName = (id) => staff.find((s) => s.id === id)?.name || "Unknown";
  return (
    <div data-testid="services-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-neutral-500">Each service has its own duration and buffers — no universal appointment length.</p>
        <Btn data-testid="service-add-button" onClick={() => setModal({ mode: "add" })}><Plus size={15} /> Add Service</Btn>
      </div>
      {services.length === 0 ? (
        <Empty testid="services-empty" icon={Briefcase} title="No services yet" body="Create your first bookable service — e.g. Consultation, 30 minutes — then qualify staff for it." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {services.map((s) => (
            <Card key={s.id} className="p-5" data-testid={`service-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2.5">
                    <p className="text-sm font-bold text-[#111111]">{s.name}</p>
                    <Pill tone={s.active ? "black" : "outline"}>{s.active ? "Active" : "Inactive"}</Pill>
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">
                    {s.duration_min} min
                    {s.buffer_before_min ? ` · ${s.buffer_before_min} min buffer before` : ""}
                    {s.buffer_after_min ? ` · ${s.buffer_after_min} min buffer after` : ""}
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">
                    Qualified: {(s.staff_ids || []).length ? s.staff_ids.map(staffName).join(", ") : "nobody yet"}
                  </p>
                </div>
                <button data-testid={`service-edit-${s.id}`} aria-label="Edit service" onClick={() => setModal({ mode: "edit", service: s })}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                  <Pencil size={14} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {modal && <ServiceModal service={modal.mode === "edit" ? modal.service : null} staff={staff} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />}
    </div>
  );
}

export function BookingSettingsPanel({ settings, onChanged }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // Always hydrate from the latest server state — never keep stale initial values.
  useEffect(() => {
    if (settings) {
      setForm({
        tz: settings.timezone,
        min: settings.min_booking_notice_hours,
        cancel: settings.cancel_notice_hours,
      });
    }
  }, [settings]);

  if (!form) return <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api.put("/booking/settings", { timezone: form.tz.trim(), min_booking_notice_hours: Number(form.min), cancel_notice_hours: Number(form.cancel) });
      setSaved(true); // only shown after confirmed persistence
      onChanged();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4" data-testid="booking-settings-panel">
      <Card className="p-6">
        <p className="mb-4 text-xs font-bold uppercase tracking-wider text-neutral-500">Scheduling timezone & policies</p>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Business timezone (IANA)">
            <input data-testid="settings-timezone-input" className={inputCls} value={form.tz} onChange={(e) => setForm((f) => ({ ...f, tz: e.target.value }))} list="tz-list" required />
            <datalist id="tz-list">
              {["Europe/London", "Europe/Dublin", "Europe/Paris", "Asia/Kolkata", "Asia/Dubai", "America/New_York", "America/Los_Angeles", "Australia/Sydney"].map((z) => <option key={z} value={z} />)}
            </datalist>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Minimum booking notice (hours)">
              <input data-testid="settings-min-notice-input" type="number" min={0} max={720} className={inputCls} value={form.min} onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))} />
            </Field>
            <Field label="Cancellation notice (hours)">
              <input data-testid="settings-cancel-notice-input" type="number" min={0} max={720} className={inputCls} value={form.cancel} onChange={(e) => setForm((f) => ({ ...f, cancel: e.target.value }))} />
            </Field>
          </div>
          {error && <p data-testid="settings-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
          {saved && <p data-testid="settings-saved" className="rounded-xl bg-neutral-100 px-4 py-2.5 text-sm text-neutral-700">Settings saved.</p>}
          <Btn data-testid="settings-save-button" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Settings"}</Btn>
        </form>
      </Card>
      <Notice
        testid="booking-policy-notice"
        title="Who these policies apply to"
        body="Notice limits will apply to customer-facing and AI bookings (later phases). Signed-in staff can always override the notice window when booking manually. There is no maximum advance-booking limit."
      />
    </div>
  );
}
